import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { useT } from "../lib/i18n";
import { isNative } from "../lib/native";
import { QRCodeSVG } from "qrcode.react";
import {
    DeviceMobile, AppleLogo, AndroidLogo, ShareNetwork,
    PlusSquare, X, QrCode, CheckCircle, DotsThreeVertical,
} from "@phosphor-icons/react";

/**
 * "Download app" CTA + modal.
 *
 * Three real platforms, three real install paths:
 *
 *   • iOS Safari  → Add to Home Screen (Share → +). We CAN'T trigger this
 *                   programmatically — Apple disables `beforeinstallprompt`.
 *                   We show step-by-step instructions instead.
 *   • Android     → `beforeinstallprompt` event fires once Chrome decides
 *                   the app is installable; we capture it and call .prompt()
 *                   on click. Falls back to instructions if event missed.
 *   • Desktop     → A QR code that points to the production URL so the
 *                   visitor scans with their phone and installs from there.
 *
 * Already running standalone or inside the Capacitor shell? Hide the button.
 */
function isIos() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iOSUa = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = /Mac/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
    return iOSUa || iPadOS;
}

function isAndroid() {
    if (typeof navigator === "undefined") return false;
    return /Android/i.test(navigator.userAgent || "");
}

function isStandalone() {
    if (typeof window === "undefined") return false;
    return Boolean(window.navigator.standalone) || window.matchMedia("(display-mode: standalone)").matches;
}

function isWebview() {
    if (typeof navigator === "undefined") return false;
    return /(FBAN|FBAV|Instagram|Line|Twitter|TikTok|MicroMessenger)/i.test(navigator.userAgent || "");
}

export default function DownloadAppButton() {
    const t = useT();
    const [open, setOpen] = useState(false);
    const [installEvent, setInstallEvent] = useState(null);
    const [justInstalled, setJustInstalled] = useState(false);

    useEffect(() => {
        const onBefore = (e) => {
            // Stop Chrome's mini-info-bar; we'll use this event ourselves.
            e.preventDefault();
            setInstallEvent(e);
        };
        const onInstalled = () => {
            setJustInstalled(true);
            setInstallEvent(null);
            setTimeout(() => setOpen(false), 1500);
        };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    // Hide entirely when there's nothing to install.
    if (isNative() || isStandalone()) return null;

    return (
        <>
            <Button
                size="lg"
                variant="outline"
                onClick={() => setOpen(true)}
                className="rounded-xl border-[#184A30] text-[#184A30] hover:bg-[#184A30]/5 h-12 inline-flex items-center gap-2"
                data-testid="hero-cta-download-app"
            >
                <DeviceMobile size={18} weight="duotone" />
                {t("download.button")}
            </Button>

            {open && (
                <DownloadModal
                    onClose={() => setOpen(false)}
                    installEvent={installEvent}
                    setInstallEvent={setInstallEvent}
                    justInstalled={justInstalled}
                />
            )}
        </>
    );
}

function DownloadModal({ onClose, installEvent, setInstallEvent, justInstalled }) {
    const t = useT();
    const platform = isIos() ? "ios" : isAndroid() ? "android" : "desktop";
    const inWebview = isWebview();

    return (
        <div
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={onClose}
            data-testid="download-modal"
        >
            <div
                className="w-full max-w-md bg-[#FBF9F6] text-[#112318] rounded-3xl p-7 shadow-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A5C50]">
                            {t("download.modal_eyebrow")}
                        </p>
                        <h3 className="serif text-2xl mt-1.5">{t("download.modal_title")}</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 p-1.5 rounded-full hover:bg-black/5 transition-colors"
                        aria-label={t("download.close")}
                        data-testid="download-modal-close"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {justInstalled ? (
                    <div className="mt-6 rounded-xl bg-[#184A30]/10 border border-[#184A30]/20 p-5 text-center" data-testid="download-installed">
                        <CheckCircle size={32} weight="duotone" className="text-[#184A30] mx-auto" />
                        <p className="mt-2 font-semibold">{t("download.installed_title")}</p>
                        <p className="text-sm text-[#4A5C50] mt-1">{t("download.installed_body")}</p>
                    </div>
                ) : platform === "ios" ? (
                    <IosInstructions t={t} inWebview={inWebview} />
                ) : platform === "android" ? (
                    <AndroidInstructions
                        t={t}
                        installEvent={installEvent}
                        setInstallEvent={setInstallEvent}
                        inWebview={inWebview}
                    />
                ) : (
                    <DesktopQR t={t} />
                )}

                <p className="mt-6 text-[11px] text-[#4A5C50]/70 text-center">
                    {t("download.footnote")}
                </p>
            </div>
        </div>
    );
}

function IosInstructions({ t, inWebview }) {
    if (inWebview) {
        return (
            <div className="mt-5 rounded-xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-4 text-sm text-[#8C6B21]" data-testid="download-webview-warning">
                {t("download.webview_warning")}
            </div>
        );
    }
    return (
        <div className="mt-6" data-testid="download-ios-steps">
            <p className="text-[12px] text-[#4A5C50] bg-[#184A30]/[0.04] border border-[#184A30]/15 rounded-lg px-3 py-2 mb-4">
                {t("download.steps_hint") || "Do these in Safari — not on this page. The Share button is at the bottom of your screen."}
            </p>
            <ol className="space-y-4">
                <Step n="1" Icon={AppleLogo} title={t("download.ios_step1_title")} body={t("download.ios_step1_body")} />
                <Step n="2" Icon={ShareNetwork} title={t("download.ios_step2_title")} body={t("download.ios_step2_body")} />
                <Step n="3" Icon={PlusSquare} title={t("download.ios_step3_title")} body={t("download.ios_step3_body")} />
            </ol>
            <div className="mt-6 flex flex-col items-center" aria-hidden="true">
                <p className="text-[11px] text-[#4A5C50] mb-1.5">{t("download.ios_arrow_label") || "Tap this in Safari"}</p>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#184A30] text-white shadow-lg animate-bounce">
                    <ShareNetwork size={22} weight="bold" />
                </div>
                <svg width="14" height="36" viewBox="0 0 14 36" className="mt-1 text-[#184A30]/50">
                    <path d="M7 0 V30 M2 25 L7 32 L12 25" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p className="text-[10px] text-[#4A5C50]/70 mt-1">{t("download.ios_arrow_caption") || "It's at the very bottom of Safari"}</p>
            </div>
        </div>
    );
}

function AndroidInstructions({ t, installEvent, setInstallEvent, inWebview }) {
    const [busy, setBusy] = useState(false);

    const trigger = async () => {
        if (!installEvent) return;
        setBusy(true);
        try {
            await installEvent.prompt();
            await installEvent.userChoice;
        } catch { /* user cancelled or browser denied */ }
        setInstallEvent(null);
        setBusy(false);
    };

    if (inWebview) {
        return (
            <div className="mt-5 rounded-xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-4 text-sm text-[#8C6B21]" data-testid="download-webview-warning">
                {t("download.webview_warning")}
            </div>
        );
    }
    if (installEvent) {
        return (
            <div className="mt-6 text-center" data-testid="download-android-prompt">
                <AndroidLogo size={40} weight="duotone" className="text-[#184A30] mx-auto" />
                <p className="mt-3 text-sm text-[#4A5C50]">{t("download.android_ready_body")}</p>
                <Button
                    type="button"
                    onClick={trigger}
                    disabled={busy}
                    className="mt-5 w-full h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
                    data-testid="download-android-install"
                >
                    {busy ? t("download.android_installing") : t("download.android_install")}
                </Button>
            </div>
        );
    }
    // Fallback: Chrome may not have fired beforeinstallprompt yet (or the
    // user is on Firefox / Samsung Internet). Show the manual menu route.
    return (
        <div className="mt-6" data-testid="download-android-steps">
            <p className="text-[12px] text-[#4A5C50] bg-[#184A30]/[0.04] border border-[#184A30]/15 rounded-lg px-3 py-2 mb-4">
                {t("download.steps_hint") || "Do these in Chrome — not on this page. The menu is the three dots in the top-right."}
            </p>
            <ol className="space-y-4">
                <Step n="1" Icon={AndroidLogo} title={t("download.android_step1_title")} body={t("download.android_step1_body")} />
                <Step n="2" Icon={PlusSquare} title={t("download.android_step2_title")} body={t("download.android_step2_body")} />
            </ol>
            <div className="mt-6 flex justify-end items-start" aria-hidden="true">
                <div className="text-center">
                    <p className="text-[11px] text-[#4A5C50] mb-1.5">{t("download.android_arrow_label") || "Look up here in Chrome"}</p>
                    <svg width="60" height="36" viewBox="0 0 60 36" className="text-[#184A30]/50 ml-auto">
                        <path d="M58 4 H 14 M22 4 L14 4 L 14 12" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#184A30] text-white shadow-lg animate-bounce mt-1">
                        <DotsThreeVertical size={20} weight="bold" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function DesktopQR({ t }) {
    // Point the QR at /install (not the homepage), so when the user scans
    // with their phone they land on a dedicated page that immediately
    // shows the right install steps for their OS — instead of dropping
    // them on the marketing site and hoping the browser fires a prompt
    // (which iOS Safari NEVER does, and Android Chrome only does after
    // multiple engaged visits).
    const origin = (typeof window !== "undefined" ? window.location.origin : "").replace(/\/$/, "");
    const url = `${origin}/install?from=qr`;
    return (
        <div className="mt-6" data-testid="download-desktop-qr">
            <div className="flex items-start gap-3">
                <QrCode size={22} weight="duotone" className="text-[#184A30] shrink-0 mt-1" />
                <p className="text-sm text-[#4A5C50] leading-relaxed">{t("download.desktop_body")}</p>
            </div>
            <div className="mt-5 flex justify-center p-5 rounded-2xl bg-white border border-[#E5E0D8]">
                <QRCodeSVG
                    value={url}
                    size={180}
                    fgColor="#0A1F16"
                    bgColor="#FFFFFF"
                    includeMargin={false}
                />
            </div>
            <p className="mt-3 text-[11px] mono text-[#4A5C50] text-center break-all">{url}</p>
        </div>
    );
}

function Step({ n, Icon, title, body }) {
    return (
        <li className="flex gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-[#184A30] text-[#FBF9F6] flex items-center justify-center font-semibold">
                {n}
            </div>
            <div className="flex-1">
                <p className="font-semibold text-sm flex items-center gap-2">
                    <Icon size={16} weight="duotone" className="text-[#184A30]" />
                    {title}
                </p>
                <p className="mt-1 text-[13px] text-[#4A5C50] leading-relaxed">{body}</p>
            </div>
        </li>
    );
}
